import * as Styled from '../MatchmakerAttendeeModal/MatchmakerAttendeeModal.styles';

import {
  Box,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';

import { MatchmakerLogo } from "../../assets";
import logo from "../../assets/images/QR-code-logo.png";

import BaseModal from "../BaseModal/BaseModal";
import { useTheme } from "@mui/material";
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import imageCompression from 'browser-image-compression';
import { toBase64 } from '../../utils/utils';
import { POST_EVENT_IMAGE, GET_MATCHMAKER_IMAGES_ENDPOINT } from '../../services/endpoints';
import axiosAPI from '../../services/axios';
import { MatchmakerMatch, Step } from '../../models/matchmakerModel';
import MatchmakerStatusModal from './MatchmakerStatusModal';
import { validateFileFormat } from '../../utils/photo_utils';

type MatchmakerAttendeeModalProps = {
  open: any;
  onClose: any;
  eventId: string;
  matchingCompleted?: boolean;
  matches: MatchmakerMatch[];
}

const MatchmakerAttendeeModal = ({
  open,
  onClose,
  eventId,
  matchingCompleted,
  matches,
}: MatchmakerAttendeeModalProps) => {
  const theme = useTheme();

  const user = useSelector((state: any) => state.user);

  const [currentStep, setCurrentStep] = useState(Step.UPLOAD)
  const [uploadPhoto, setUploadPhoto] = useState<File | null>();
  const [imageSrc, setImageSrc] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [uploadPhotoLoading, setUploadPhotoLoading] = useState(false);
  const [hydratedMatches, setHydratedMatches] = useState<MatchmakerMatch[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    const file = event.target.files?.[0]

    if (file) {
      setUploadPhoto(file);
      const reader = new FileReader();

      // Set up the reader onload function
      reader.onload = (e: any) => {
        // Set the image source with the data URL
        setImageSrc(e.target.result);
      };

      // Read the file as a data URL
      reader.readAsDataURL(file);

      const isValidFile = validateFileFormat(file);

      if (!isValidFile) {
        setErrorMessage("One or more files are not supported. Please upload supported file type.");
      } else {
        setErrorMessage('');
      }
    }
  }

  const uploadImage = async (file: File | undefined) => {
    const options = {
      maxSizeMB: 3.0, // (max file size in MB)
      maxWidthOrHeight: 1920, // compress the image to this maximum width or height
      useWebWorker: true,
    };
    if (!file) return '';
    try {
      const compressedFile = await imageCompression(file, options);
      return await toBase64(compressedFile);
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error; // Throw the error so it can be caught in the parent scope
    }
  };

  const postEventImage = async (eventId: string, photoUpload: any) => {
    if (typeof eventId !== 'string') return;
    try {
      await axiosAPI.post(POST_EVENT_IMAGE(eventId), photoUpload);
    } catch (error) {
      console.error('Error posting event image:', error);
      throw error; // Throw the error so it can be caught in the parent scope
    }
  };

  const handleInputClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    const onPhotoUpload = async () => {
      try {
        if (uploadPhoto && !errorMessage) {
          console.log('UPLOADING', uploadPhoto)
          const base64Image = await uploadImage(uploadPhoto);
          const newPhotoUpload = {
            name: `matchmaker-${eventId}-${user.email}$${uploadPhoto?.name}`,
            data: base64Image,
            uploaderName: user.name,
            uploaderEmail: user.email,
          };
          // upload photo to api 
          setUploadPhotoLoading(true);
          await postEventImage(eventId, newPhotoUpload);

        }
      } catch (error) {
        setErrorMessage('Upload failed. Please try again.');
      } finally {
        setUploadPhotoLoading(false);

        if (!errorMessage) {
          setCurrentStep(Step.SEARCHING);
        }
      }
    }
    if (uploadPhoto) {
      onPhotoUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, uploadPhoto, user.email, user.name])


  useEffect(() => {
    const fetchMatchmakerPhotos = async (email: string) => {
      return await axiosAPI.get(GET_MATCHMAKER_IMAGES_ENDPOINT(eventId, email));
    }

    if (matchingCompleted) {
      // add step to call api after photo upload
      matches.forEach((match) => {
        fetchMatchmakerPhotos(match.email).then((response) => {
          console.log('fetchMatchmakerPhotos response', response)
          if (response.data.result) {
            console.log('response.data[0].s3Url', response.data.result.s3Url)
            setHydratedMatches((prevItems) => [...prevItems, { name: match.name, email: match.email, title: match.title, picture: response.data.result.s3Url }]);
          }
        });

      });
      setUploadPhotoLoading(false);
      if (matches.length === 1) {
        setCurrentStep(Step.RESULT);
      } else if (matches.length > 1) {
        setCurrentStep(Step.GROUP_RESULT);
      }
    }
  }, [eventId, matches, matchingCompleted, user.email])

  useEffect(() => {
    if (currentStep === Step.UPLOAD && !uploadPhotoLoading) {
      const timer = setTimeout(() => {
        setCurrentStep(Step.TIMEOUT);
      }, 300000);

      return () => clearTimeout(timer);
    }
  })

  const renderContainer = (current: Step): JSX.Element | null => {

    switch (current) {
      case Step.UPLOAD:
        return (
          <Box>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              rowGap="5px"
              marginTop="10px"
              marginBottom="20px"
            >
              <Typography variant="body2" sx={{ textAlign: "center" }}>
                Before you’re matched, please provide a photo of yourself so that your partner can find you.
              </Typography>
              <Typography variant="caption" sx={{ fontStyle: "italic", color: theme.colors.neutral.darkGray, mb: 0.5 }}>
                Use a photo that clearly shows your likeness
              </Typography>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              rowGap="15px"
            >
              <Styled.ModalButton onClick={handleInputClick}>
                Take a Selfie
                <input
                  type='file'
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </Styled.ModalButton>
              <Typography variant="body2" sx={{ textAlign: "center" }}>
                Or
              </Typography>
              <Styled.ModalButton onClick={handleInputClick}>
                Upload a Photo
                <input
                  type='file'
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </Styled.ModalButton>
            </Box>
            {errorMessage ? (
              <Typography variant="modalBody" sx={{ display: 'flex', color: theme.colors.action.failure, pt: 2, mb: 0.5 }}>
                {errorMessage}
              </Typography>
            ) : null}
          </Box>
        )
      case Step.SEARCHING:
        return (
         <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          rowGap="10px"
          marginTop="10px"
          marginBottom="20px" 
        >
          <Box sx={{height: "120px", width: "120px", borderRadius: "50%", overflow: "hidden", alignItems: "center"}}>
            <Styled.PhotoContainer src={imageSrc || logo}></Styled.PhotoContainer>
          </Box>
          <Typography variant="body2" sx={{ textAlign: "center" }}>
            Thanks for your upload!
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            We are now finding you a partner...
            <CircularProgress size={20} />
          </Typography>
          <Typography 
            variant="caption"
            sx={{
              mt: 0.5,
              textAlign: "center",
              fontStyle: "italic",
              color: theme.colors.neutral.darkGray
            }}
            >
            Do not navigate away from this screen or you will miss your match!
          </Typography>
        </Box> 
        )
      
      case Step.RESULT: 
      return (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          rowGap="10px"
          marginTop="10px"
          marginBottom="20px" 
        >
          <Typography variant="body2" sx={{ textAlign: "center" }}>
            You have been matched with the following partner. Find each other and enjoy your new connection!
          </Typography>
          <Box 
            display="flex" 
            width="100%" 
            marginY="10px" 
            justifyContent="space-evenly"
          >
            { hydratedMatches.map((match: MatchmakerMatch) => (
              <Grid display="flex" flexDirection="column"  sx={{alignItems:"center"}}>

                  <Box sx={{ height: "120px", width: "120px", display: "flex", borderRadius: "50%", overflow: "hidden", alignItems: "center" }}>
                  <Styled.PhotoContainer src={match.picture || logo}></Styled.PhotoContainer> 
                </Box>
                <Box sx={{ width: "120px", display: "flex"}}>
                <Typography variant="body1" sx={{ textAlign: "center", justifyContent:"center", display: "flex", maxWidth: "120px" }}>
                  {match.name}
                </Typography>
                </Box>
              </Grid>
              ))
            }
          </Box>
          <Typography variant="caption" sx={{ fontStyle: "italic", color: theme.colors.neutral.darkGray, mb: 0.5 }}>
              When you’re done, feel free to end this session below.
          </Typography>
          <Styled.ModalButton onClick={() => setCurrentStep(Step.SUCCESS)}>End Matchmaker</Styled.ModalButton>
        </Box> 
      )

      case Step.GROUP_RESULT: 
      return (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          rowGap="10px"
          marginTop="10px"
          marginBottom="20px" 
        >
          <Typography variant="body2" sx={{ textAlign: "center" }}>
            You have been matched with the following partners. Find each other and enjoy your new connections!
          </Typography>
         
            <Box
              display="flex"
              justifyContent="space-evenly"
              marginY="10px"
              width="100%"
            >
              {hydratedMatches.map((match) => (
            <Grid display="flex" flexDirection="column"  sx={{alignItems:"center"}}>
                              <Box sx={{ height: "120px", width: "120px", display: "flex", borderRadius: "50%", overflow: "hidden", alignItems: "center" }}>
                                <Styled.PhotoContainer src={match.picture || logo}></Styled.PhotoContainer>
                              </Box>
                              <Box sx={{ width: "120px", display: "flex"}}>
                              <Typography variant="body1" sx={{ textAlign: "center", justifyContent: "center", display: "flex", maxWidth: "120px" }}>
                                {match.name}
                              </Typography>
                              </Box>
                            </Grid>))
              }
            </Box>
            <Typography variant="caption" sx={{ fontStyle: "italic", color: theme.colors.neutral.darkGray, mb: 0.5 }}>
              When you’re done, feel free to end this session below.
            </Typography>
            <Styled.ModalButton onClick={() => setCurrentStep(Step.SUCCESS)}>End Matchmaker</Styled.ModalButton>
          </Box>
        )
      default:
        break;
    }

    return null;
  }

  return (
    <>
      {currentStep === Step.SUCCESS || currentStep === Step.TIMEOUT ?
        <MatchmakerStatusModal open={open} onClose={onClose} currentStep={currentStep} />
        :
        <BaseModal
          open={open}
          onClose={onClose}
          desktopWidth="511px"
        >
          <Stack
            direction="column"
            width="100%"
            style={{ marginTop: theme.spacing(1) }}
          >
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              columnGap="10px"
            >
              <Styled.MatchmakerLogo src={MatchmakerLogo} />
              <Styled.ModalTitle variant='h2'>Matchmaker</Styled.ModalTitle>
              {renderContainer(currentStep)}
            </Box>

          </Stack>
        </BaseModal>
      }
    </>
  )
};

export default MatchmakerAttendeeModal;